/**
 * RegisterForm - User registration form with invite token support
 * Feature: 647-invite-registration
 * Feature: 892-invite-redirect - Post-authentication redirect support
 *
 * When inviteToken is provided, uses registerWithInvite mutation.
 * Otherwise, uses standard register (which will fail since invite-only is enforced).
 *
 * When returnUrl is provided, redirects to that URL after successful registration
 * (with security validation to prevent open redirects).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/services/auth';
import { Button, Text } from '@/components/atoms';
import { Stack } from '@/components/layout';
import { FormField } from '@/components/composed';
import { useRegisterWithInviteGraphQL } from '@/hooks/graphql/useInviteGraphQL';
import { validateReturnUrl } from '@/utils/urls';
import logger from '@/utils/logger';

interface RegisterFormProps {
  inviteToken?: string;
  // Feature: 892-invite-redirect - Optional destination URL after registration
  returnUrl?: string;
}

export function RegisterForm({ inviteToken, returnUrl }: RegisterFormProps) {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { registerWithInvite, loading: inviteLoading, error: inviteError } = useRegisterWithInviteGraphQL();

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    displayName: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (inviteToken) {
        // Use invite-based registration
        const result = await registerWithInvite({
          email: formData.email,
          username: formData.username,
          displayName: formData.displayName,
          password: formData.password,
          inviteToken,
        });

        if (result) {
          // Feature: 892-invite-redirect - Redirect to returnUrl if valid, otherwise home
          // Validate returnUrl to prevent open redirect attacks
          const validatedUrl = validateReturnUrl(returnUrl);
          const destination = validatedUrl || '/';

          // Full page redirect to ensure auth context properly initializes
          // The token and user are already stored in localStorage by the hook
          window.location.href = destination;
          return;
        }
      } else {
        // Standard registration (will fail if invite-only is enforced)
        await register(formData.email, formData.username, formData.displayName, formData.password);
        // Feature: 892-invite-redirect - Redirect to returnUrl if valid, otherwise home
        const validatedUrl = validateReturnUrl(returnUrl);
        navigate(validatedUrl || '/');
      }
    } catch (err: any) {
      logger.error('Auth', 'Registration failed', err);

      // Extract error message from GraphQL error or response
      const errorMessage =
        err.graphQLErrors?.[0]?.message ||
        err.response?.data?.error?.message ||
        err.message ||
        'Registration failed';

      // Handle specific error codes
      const errorCode = err.graphQLErrors?.[0]?.extensions?.code;
      if (errorCode === 'TOKEN_INVALID') {
        setError('This invite link is no longer valid. It may have been exhausted or expired.');
      } else if (errorCode === 'CONFLICT') {
        setError('An account with this email or username already exists.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || inviteLoading;
  const displayError = error || (inviteError?.message);

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <Stack direction="vertical" spacing={4}>
        {displayError && (
          <Text className="text-danger bg-danger/10 border border-danger/30 rounded p-3">
            {displayError}
          </Text>
        )}
        <FormField
          id="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          autoComplete="email"
          required
        />
        <FormField
          id="username"
          label="Username"
          type="text"
          placeholder="Username"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          autoComplete="username"
          required
        />
        <FormField
          id="displayName"
          label="Display Name"
          type="text"
          placeholder="Display Name"
          value={formData.displayName}
          onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
          autoComplete="off"
          required
        />
        <FormField
          id="password"
          label="Password"
          type="password"
          placeholder="Password (min 8 characters)"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <Button type="submit" disabled={isLoading} loading={isLoading} variant="primary">
          {isLoading ? 'Registering...' : 'Register'}
        </Button>
      </Stack>
    </form>
  );
}
